import { create } from 'zustand';

/**
 * Reality check (design "Responsible play": "Remind me every 60 min").
 * A per-device preference; the reminder counts the time since the signed-in player's browser session
 * started. The session (`{ user, start, ack }`) lives in sessionStorage, so a reload neither restarts the
 * clock nor shows an already acknowledged reminder again; time spent as a guest is not counted, and a
 * sign-in / sign-up / account switch starts a fresh session.
 */
export const REALITY_EVERY_MIN = 60;
const KEY = 'tg.realityCheck';
const SESSION_KEY = 'tg.reality';
/** pre-fix key: a start stamp set on the first page view, even as a guest */
const LEGACY_START_KEY = 'tg.sessionStart';

export interface RealitySession {
  user: string;
  start: number;
  /** reminders acknowledged in this session */
  ack: number;
}

function load(): boolean {
  try {
    const v = localStorage.getItem(KEY);
    return v === null ? true : v === 'on';
  } catch {
    return true;
  }
}

function readStored(): RealitySession | null {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const v = JSON.parse(raw) as Partial<RealitySession>;
    if (typeof v.user !== 'string' || !Number.isFinite(v.start) || !Number.isInteger(v.ack) || (v.ack as number) < 0) return null;
    return { user: v.user, start: v.start as number, ack: v.ack as number };
  } catch {
    return null;
  }
}

function store(s: RealitySession): void {
  try {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(s));
    sessionStorage.removeItem(LEGACY_START_KEY);
  } catch {
    /* private mode / blocked storage: the session lives in memory only */
  }
}

/** The reality-check session of `user`: the stored one, or a new one starting `now` (other user / none / bad data). */
export function realitySession(user: string, now = Date.now()): RealitySession {
  const s = readStored();
  if (s && s.user === user && s.start <= now) return s;
  const fresh = { user, start: now, ack: 0 };
  store(fresh);
  return fresh;
}

/** Minutes played at which the next reminder is due, given the last acknowledged one. */
export function nextReminderAt(startMs: number, acknowledged: number, everyMin = REALITY_EVERY_MIN): number {
  return startMs + (acknowledged + 1) * everyMin * 60_000;
}

interface RealityState {
  enabled: boolean;
  /** current signed-in session, null for guests */
  session: RealitySession | null;
  setEnabled(v: boolean): void;
  /** called with the signed-in user id (null when signed out): keeps or starts the session */
  bindUser(user: string | null, now?: number): void;
  /** marks every reminder due by `now` as seen (several may have passed while the tab slept) */
  acknowledge(now?: number): void;
}

export const useRealityCheck = create<RealityState>()((set, get) => ({
  enabled: load(),
  session: null,
  setEnabled(enabled) {
    try {
      localStorage.setItem(KEY, enabled ? 'on' : 'off');
    } catch {
      /* ignore */
    }
    set({ enabled });
  },
  bindUser(user, now = Date.now()) {
    const cur = get().session;
    if (user === null) {
      if (cur) set({ session: null });
      return;
    }
    if (cur?.user === user) return;
    set({ session: realitySession(user, now) });
  },
  acknowledge(now = Date.now()) {
    const s = get().session;
    if (!s) return;
    const due = Math.floor((now - s.start) / (REALITY_EVERY_MIN * 60_000));
    if (due <= s.ack) return;
    const next = { ...s, ack: due };
    store(next);
    set({ session: next });
  },
}));
